/**
 * A name, presented as a name.
 *
 * Names reach the screen carrying no case information at all, from two
 * directions. The ERP shouts: `MARIA DA SILVA`, because that is how it was
 * typed years ago. And a name that fell back to a check's normalized evidence
 * whispers: `maria da silva`, because `normalizeTerm` lower-cases in order to
 * compare. Rendering either as-is to a pharmacist reading all day is a wall of
 * capitals or a wall of lower case, and neither is what anybody wrote.
 *
 * So the rule is about *information*, not about shouting: when a value is
 * entirely upper case or entirely lower case, it carries no case of its own and
 * we supply the conventional one. When it is mixed -- `Maria da Silva`, read
 * from a doctor's paper -- somebody's case is already in it, and changing that
 * would be editing what a side said, which this system does not do.
 *
 * Ported from V1's `core/text/titleCase`, widened to the caseless rule when
 * patient and prescriber rows turned out to render normalized evidence.
 */
export function titleCaseName(value: string | undefined): string {
  if (value === undefined) return ''

  const caseless = value === value.toUpperCase() || value === value.toLowerCase()
  if (!caseless) return value

  /*
   * The apostrophes are here because the ported rule did not have them, and
   * `D'ÁVILA` came out as `D'ávila` -- a surname the ERP holds plenty of.
   * Both the typewriter and the typographic one, since a name can arrive
   * carrying either.
   */
  return value
    .toLowerCase()
    .replace(/(^|[\s(\-/'\u2019])([\p{L}])/gu, (_, boundary: string, letter: string) => {
      return `${boundary}${letter.toUpperCase()}`
    })
}
